import { useState, useEffect, useRef, useCallback } from 'react'
import type { CandlestickData, UTCTimestamp } from 'lightweight-charts'
import { getTradingDataWebSocketUrl, tradingDataApi } from '@/shared/api'
import { toFiniteNumber } from '@/shared/lib'
import type {
  TradingDataKline,
  TradingDataMarketState,
  TradingDataWebSocketEvent,
} from '@/shared/types'

const CANDLE_INTERVAL_SECONDS = 60
const MAX_CHART_POINTS = 240

function mapKlineToCandle(kline: TradingDataKline): CandlestickData<UTCTimestamp> | null {
  const open = toFiniteNumber(kline.open)
  const high = toFiniteNumber(kline.high)
  const low = toFiniteNumber(kline.low)
  const close = toFiniteNumber(kline.close)
  const timestampSec = Math.floor(kline.openTime / 1000)

  if (!open || !high || !low || !close || !timestampSec) return null

  return { time: timestampSec as UTCTimestamp, open, high, low, close }
}

export interface TradingDataState {
  tokenAddress: string | null
  pairLabel: string
  price: number
  priceFlash: 'up' | 'down' | null
  openPrice: number
  high24h: number
  low24h: number
  volume24h: number
  isLive: boolean
  candles: CandlestickData<UTCTimestamp>[]
}

export interface UseTradingDataResult extends TradingDataState {
  /** Call when series ref becomes available to push live updates */
  onCandleUpdate: (cb: CandleUpdateCallback) => void
}

type CandleUpdateCallback = (candle: CandlestickData<UTCTimestamp>, isNew: boolean) => void

export function useTradingData(preferredSymbol = 'BTC'): UseTradingDataResult {
  const [tokenAddress, setTokenAddress] = useState<string | null>(null)
  const [pairLabel, setPairLabel] = useState(`${preferredSymbol} / USDT`)
  const [price, setPrice] = useState(0)
  const [priceFlash, setPriceFlash] = useState<'up' | 'down' | null>(null)
  const [openPrice, setOpenPrice] = useState(0)
  const [high24h, setHigh24h] = useState(0)
  const [low24h, setLow24h] = useState(0)
  const [volume24h, setVolume24h] = useState(0)
  const [isLive, setIsLive] = useState(false)
  const [candles, setCandles] = useState<CandlestickData<UTCTimestamp>[]>([])

  const dataRef = useRef<CandlestickData<UTCTimestamp>[]>([])
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const candleCallbackRef = useRef<CandleUpdateCallback | null>(null)

  const onCandleUpdate = useCallback((cb: CandleUpdateCallback) => {
    candleCallbackRef.current = cb
  }, [])

  const applyMarketState = useCallback((ms: TradingDataMarketState) => {
    const cp = toFiniteNumber(ms.currentPrice)
    const h = toFiniteNumber(ms.high24h)
    const l = toFiniteNumber(ms.low24h)
    const v = toFiniteNumber(ms.volume24h)
    if (cp > 0) setPrice(cp)
    if (h > 0) setHigh24h(h)
    if (l > 0) setLow24h(l)
    if (v > 0) setVolume24h(v)
  }, [])

  const updateCandle = useCallback((nextPrice: number, timestampMs: number) => {
    if (nextPrice <= 0) return

    const arr = dataRef.current
    const bucketTime = Math.floor(timestampMs / 1000 / CANDLE_INTERVAL_SECONDS) * CANDLE_INTERVAL_SECONDS
    const candleTime = bucketTime as UTCTimestamp

    if (arr.length === 0) {
      const first: CandlestickData<UTCTimestamp> = {
        time: candleTime, open: nextPrice, high: nextPrice, low: nextPrice, close: nextPrice,
      }
      dataRef.current = [first]
      setCandles([first])
      setOpenPrice(nextPrice)
      setPrice(nextPrice)
      candleCallbackRef.current?.(first, true)
      return
    }

    const last = arr[arr.length - 1]
    const prevClose = last.close

    if (last.time === candleTime) {
      const updated: CandlestickData<UTCTimestamp> = {
        ...last, high: Math.max(last.high, nextPrice), low: Math.min(last.low, nextPrice), close: nextPrice,
      }
      dataRef.current = [...arr.slice(0, -1), updated]
      candleCallbackRef.current?.(updated, false)
    } else if (candleTime > last.time) {
      const newCandle: CandlestickData<UTCTimestamp> = {
        time: candleTime,
        open: prevClose,
        high: Math.max(prevClose, nextPrice),
        low: Math.min(prevClose, nextPrice),
        close: nextPrice,
      }
      const next = [...arr, newCandle]
      if (next.length > MAX_CHART_POINTS) {
        const trimmed = next.slice(-MAX_CHART_POINTS)
        dataRef.current = trimmed
        setCandles(trimmed)
      } else {
        dataRef.current = next
      }
      candleCallbackRef.current?.(newCandle, true)
    }

    setPrice(nextPrice)
    setPriceFlash(nextPrice >= prevClose ? 'up' : 'down')
    window.setTimeout(() => setPriceFlash(null), 300)
  }, [])

  // Reset state when symbol changes
  useEffect(() => {
    setTokenAddress(null)
    setPrice(0)
    setPriceFlash(null)
    setOpenPrice(0)
    setHigh24h(0)
    setLow24h(0)
    setVolume24h(0)
    setIsLive(false)
    setCandles([])
    dataRef.current = []
    candleCallbackRef.current = null
  }, [preferredSymbol])

  // Load initial candles + market snapshot
  useEffect(() => {
    let canceled = false

    const loadInitialData = async () => {
      try {
        const tokens = await tradingDataApi.getTokens()
        if (canceled || tokens.length === 0) return

        const preferred = tokens.find(t => {
          const sym = t.config.symbol.toUpperCase().replace('USDT', '')
          return sym === preferredSymbol.toUpperCase()
        }) ?? tokens[0]

        setTokenAddress(preferred.address)
        const baseSymbol = preferred.config.symbol.toUpperCase().replace('USDT', '')
        setPairLabel(`${baseSymbol} / USDT`)

        const [klinesResponse, marketState] = await Promise.all([
          tradingDataApi.getKlines(preferred.address, '1m', MAX_CHART_POINTS).catch(() => null),
          tradingDataApi.getMarketState(preferred.address).catch(() => null),
        ])

        if (canceled) return

        let mapped = (klinesResponse?.klines ?? [])
          .map(mapKlineToCandle)
          .filter((c): c is CandlestickData<UTCTimestamp> => c !== null)

        if (mapped.length > 0) {
          dataRef.current = mapped
          setCandles(mapped)
          setOpenPrice(mapped[0].open)
          setPrice(mapped[mapped.length - 1].close)
        }

        if (marketState) {
          applyMarketState(marketState)
          if (mapped.length === 0) {
            const fp = toFiniteNumber(marketState.currentPrice)
            if (fp > 0) updateCandle(fp, Date.now())
          }
        }
      } catch {
        // Trading data system unavailable
      }
    }

    void loadInitialData()
    return () => { canceled = true }
  }, [preferredSymbol, applyMarketState, updateCandle])

  // WebSocket live updates
  useEffect(() => {
    if (!tokenAddress) return

    let stopped = false

    const connect = () => {
      if (stopped) return

      const ws = new WebSocket(getTradingDataWebSocketUrl())
      wsRef.current = ws

      ws.addEventListener('open', () => {
        if (stopped) return
        setIsLive(true)
        ws.send(JSON.stringify({ type: 'subscribe', tokenAddress }))
        ws.send(JSON.stringify({ type: 'get_market', tokenAddress }))
      })

      ws.addEventListener('message', event => {
        if (typeof event.data !== 'string') return
        let payload: TradingDataWebSocketEvent
        try { payload = JSON.parse(event.data) as TradingDataWebSocketEvent } catch { return }

        if (payload.type === 'price_update') {
          if (payload.data.tokenAddress !== tokenAddress) return
          const np = toFiniteNumber(payload.data.price)
          const ts = toFiniteNumber(payload.data.timestamp)
          if (np <= 0 || ts <= 0) return
          updateCandle(np, ts)
          return
        }
        if (payload.type === 'market_state') {
          if (payload.data.tokenAddress !== tokenAddress) return
          applyMarketState(payload.data)
        }
      })

      ws.addEventListener('close', () => {
        setIsLive(false)
        if (!stopped) reconnectTimeoutRef.current = setTimeout(connect, 3000)
      })

      ws.addEventListener('error', () => { ws.close() })
    }

    connect()

    return () => {
      stopped = true
      setIsLive(false)
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) {
        wsRef.current.close()
      }
      wsRef.current = null
    }
  }, [tokenAddress, applyMarketState, updateCandle])

  return {
    tokenAddress, pairLabel, price, priceFlash, openPrice,
    high24h, low24h, volume24h, isLive, candles, onCandleUpdate,
  }
}
